import { useContext, useEffect, useCallback, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SocketContext } from '@/contexts/socket';
import { integrationIdSelector } from '@/state/config';
import { hasEventsSelector, isHumanChatSelector, isSessionExpiredSelector, sessionIdSelector, sessionOverviewState, } from '@/state/session';
import { SocketEvent } from '@/ts/socket';
import * as safeStorage from '@/util/safe-storage';
import { getTimestamp } from '@/util/utils';
import { actKey } from './useActivityTracker';
const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const useSessionOverview = () => {
    const setSessionOverview = useSetRecoilState(sessionOverviewState);
    const socket = useContext(SocketContext);
    const sessionId = useRecoilValue(sessionIdSelector);
    const hasEvents = useRecoilValue(hasEventsSelector);
    const integrationId = useRecoilValue(integrationIdSelector);
    const key = actKey(integrationId);
    const isSessionExpired = useRecoilValue(isSessionExpiredSelector);
    const isHumanChat = useRecoilValue(isHumanChatSelector);
    const intervalRef = useRef(null);
    const timeoutRefs = useRef(new Set());
    useEffect(() => {
        const handleSessionOverview = (data) => {
            setSessionOverview(data);
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_overview, handleSessionOverview);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_overview, handleSessionOverview);
        };
    }, [setSessionOverview, socket]);
    const startSessionOverview = useCallback(() => {
        // Ensure that all necessary dependencies are available
        if (!socket || !sessionId || !hasEvents) {
            return;
        }
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        // Clear any existing timeouts
        timeoutRefs.current.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        timeoutRefs.current.clear();
        // Define a function to emit session overview events
        const emitSessionOverview = () => {
            const lastActivity = parseInt(safeStorage.getItem(key) || '0', 10);
            // If no activity for 5 minutes, don't ask for overview
            const expirationTime = lastActivity + FIVE_MINUTES_MS;
            if (lastActivity && expirationTime > getTimestamp() && isHumanChat) {
                if (!(socket === null || socket === void 0 ? void 0 : socket.connected)) {
                    return;
                }
                const eventName = SocketEvent.session_overview;
                const payload = {
                    session_id: sessionId,
                    timestamp: getTimestamp(),
                };
                // Emit with small delay - ensures delivery
                const timeoutId = setTimeout(() => {
                    socket.emit(eventName, payload);
                    timeoutRefs.current.delete(timeoutId);
                }, 100);
                timeoutRefs.current.add(timeoutId);
            }
        };
        // Call the function immediately if session is not expired
        if (!isSessionExpired) {
            emitSessionOverview();
        }
        // Set up an interval to emit session overview events
        intervalRef.current = setInterval(() => {
            if (!isSessionExpired) {
                emitSessionOverview();
            }
            else {
                // Stop emitting if session is expired
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        }, 30000);
    }, [hasEvents, isHumanChat, isSessionExpired, key, sessionId, socket]);
    // Clean up interval and timeouts on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            // Clean up all pending timeouts
            timeoutRefs.current.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
            timeoutRefs.current.clear();
        };
    }, []);
    return { startSessionOverview };
};
